// Datensatz (CBOR, Feldnummern) → Phase-2-Nachricht (JSON-Form, Feldnamen)
// nach docs/protokoll.md, „Phase 3": Binärrahmen". Rein, ohne Svelte; der Rest
// des Clients arbeitet danach unverändert mit Namen.
//
//   1 snapshot  [1, id, s, z, felder, topic?, e?]
//   2 delta     [2, id, b, s, z, felder, topic?, e?]
//   3 ereignis  [3, id, s, z, felder, topic?, e?]
//   4 luecke    [4, id, s, topic?, e?]
//   5 weg       [5, id, topic?]  (topic nach einer Wiederaufnahme: die id ist neu)
//   6 puls      [6]
//
// Die Metadaten (TOPICS, TYPEN, Aufzählungen) kommen als Parameter, nicht per
// Import: node (Test, strip-types) löst Importe ohne Endung nicht auf, und so
// bleibt die Datei ohne Wert-Importe. Übergeben wird das Modul schema.gen als
// Ganzes (`import * as SCHEMA`), die Aufzählungen darin sind die exportierten
// Objekte Wort → Zahl.

import type { FeldInfo, TopicInfo } from "./schema.gen";

export type Nachricht = Record<string, unknown> & { t: string };

/** Marke: Wert weglassen (unbekannte Aufzählungszahl). */
const WEG = Symbol("weg");

/** Datensatz ist strukturell kaputt (nicht: unbekannt oder überflüssig). */
export class DatensatzFehler extends Error {
  constructor(text: string) {
    super("Datensatz: " + text);
    this.name = "DatensatzFehler";
  }
}

/** Löst eine Kennung auf: `topic` (falls der Datensatz es trägt) setzt die
 *  Zuordnung, sonst gilt die gemerkte. Liefert den Instanznamen. */
export type Aufloeser = (id: number, topic: string | undefined) => string | undefined;

/** Metadaten des Schemas; `modul` ist `import * as SCHEMA from "./schema.gen"`. */
export class Umwandler {
  private readonly topics: readonly TopicInfo[];
  private readonly typen: Readonly<Record<string, readonly FeldInfo[]>>;
  private readonly modul: Record<string, unknown>;
  private readonly passt: (muster: string, instanz: string) => boolean;
  private readonly nachNr = new Map<readonly FeldInfo[], Map<number, FeldInfo>>();
  private readonly aufz = new Map<string, Map<number, string> | null>();
  private readonly gewarnt = new Set<string>();

  /** `passt`: Mustervergleich mit `*` je Segment (logik.ts `passtMuster`);
   *  als Parameter, weil diese Datei keine Wert-Importe hat (s. o.). */
  constructor(modul: object, passt: (muster: string, instanz: string) => boolean) {
    this.passt = passt;
    this.modul = modul as Record<string, unknown>;
    this.topics = this.modul.TOPICS as readonly TopicInfo[];
    this.typen = this.modul.TYPEN as Readonly<Record<string, readonly FeldInfo[]>>;
  }

  /** Familie zu einem Instanznamen: Muster `host.{host}.stats` gegen
   *  `host.forge.stats` (jedes `{…}` steht für genau ein Segment). */
  familie(instanz: string): TopicInfo | undefined {
    return this.topics.find((t) => this.passt(t.muster.replace(/\{[^}]*\}/g, "*"), instanz));
  }

  /** Datensatz → Nachricht. `undefined` = gültig, aber nichts zu tun
   *  (Nachzügler einer abbestellten Instanz, state-Instanz einer unbekannten
   *  Familie). Ein kaputter Datensatz (kein Array, unbekannter Typ, zu kurz,
   *  falsche Feldtypen) wirft `DatensatzFehler`: der Rahmen ist dann nicht
   *  verlässlich, der Aufrufer baut die Verbindung neu auf. */
  wandle(satz: unknown, aufloesen: Aufloeser): Nachricht | undefined {
    if (!Array.isArray(satz) || typeof satz[0] !== "number") throw new DatensatzFehler("kein Datensatz");
    switch (satz[0]) {
      case 1:
        return this.mitFeldern("snapshot", satz, ["id", "s", "z"], aufloesen);
      case 2:
        return this.mitFeldern("delta", satz, ["id", "b", "s", "z"], aufloesen);
      case 3:
        return this.mitFeldern("ereignis", satz, ["id", "s", "z"], aufloesen);
      case 4:
        if (satz.length < 3 || !zahlenAn(satz, 1, 3)) throw new DatensatzFehler("luecke kaputt");
        return { t: "luecke", id: satz[1], s: satz[2], ...kopfzusatz(satz, 3) };
      case 5:
        if (typeof satz[1] !== "number") throw new DatensatzFehler("weg kaputt");
        return { t: "weg", id: satz[1], ...(typeof satz[2] === "string" ? { topic: satz[2] } : {}) };
      case 6:
        return { t: "puls" };
      default:
        throw new DatensatzFehler("unbekannter Datensatztyp " + satz[0]);
    }
  }

  /** Gemeinsam für snapshot/delta/ereignis: nach dem Typ kommen die Zahlen
   *  `zahlen` (id, …), dann `felder`, dann `topic?` und `e?`. */
  private mitFeldern(t: string, satz: unknown[], zahlen: string[], aufloesen: Aufloeser): Nachricht | undefined {
    const fi = 1 + zahlen.length; // Stelle von `felder`
    if (satz.length < fi + 1 || !zahlenAn(satz, 1, fi) || !(satz[fi] instanceof Map)) throw new DatensatzFehler(t + " kaputt");
    const zusatz = kopfzusatz(satz, fi + 1);
    const name = aufloesen(satz[1] as number, zusatz.topic);
    const familie = name === undefined ? undefined : this.familie(name);
    if (!familie) {
      if (name === undefined) return undefined; // Nachzügler einer abbestellten Instanz
      // Familie aus einem neueren Schema. Ein Ereignis trotzdem quittieren,
      // sonst staut es sich beim Server (256 offene → Verbindung zu, Schleife).
      this.warneEinmal("familie:" + name, "unbekannte Topic-Familie", name);
      return t === "ereignis" ? { t: "ereignis_unbekannt", id: satz[1], s: satz[2] } : undefined;
    }
    const aus: Nachricht = { t };
    zahlen.forEach((n, i) => (aus[n] = satz[1 + i]));
    aus.d = this.wandleFelder(familie.felder, satz[fi] as Map<number | string, unknown>);
    return Object.assign(aus, zusatz);
  }

  private wandleFelder(infos: readonly FeldInfo[], felder: Map<number | string, unknown>): Record<string, unknown> {
    let index = this.nachNr.get(infos);
    if (!index) {
      index = new Map(infos.map((f) => [f.nr, f] as const));
      this.nachNr.set(infos, index);
    }
    const aus: Record<string, unknown> = {};
    for (const [k, v] of felder) {
      if (typeof k !== "number") continue;
      const info = index.get(k);
      if (!info) continue; // Feld aus einem neueren Schema
      const w = this.wandleWert(info.typ, v);
      if (w !== WEG) aus[info.name] = w;
    }
    return aus;
  }

  private wandleWert(typ: string, v: unknown): unknown {
    if (v === null || v === undefined) return null;
    if (typ.startsWith("[") && typ.endsWith("]")) {
      if (!Array.isArray(v)) return v;
      const inner = typ.slice(1, -1);
      const aus: unknown[] = [];
      for (const e of v) {
        const w = this.wandleWert(inner, e);
        if (w !== WEG) aus.push(w);
      }
      return aus;
    }
    const verschachtelt = this.typen[typ];
    if (verschachtelt) return v instanceof Map ? this.wandleFelder(verschachtelt, v) : v;
    const wort = this.aufzaehlung(typ);
    if (wort) {
      if (typeof v !== "number") return v;
      const w = wort.get(v);
      if (w === undefined) {
        this.warneEinmal(`aufz:${typ}:${v}`, "unbekannte Aufzählungszahl", `${typ} ${v}`);
        return WEG;
      }
      return w;
    }
    return v;
  }

  /** Zahl → Wort für einen Aufzählungstyp; undefined, wenn `typ` keine ist. */
  private aufzaehlung(typ: string): Map<number, string> | null {
    let m = this.aufz.get(typ);
    if (m === undefined) {
      const o = this.modul[typ];
      m = null;
      if (o && typeof o === "object" && !Array.isArray(o)) {
        const eintraege = Object.entries(o as Record<string, unknown>);
        if (eintraege.length > 0 && eintraege.every(([, z]) => typeof z === "number")) {
          m = new Map(eintraege.map(([wort, z]) => [z as number, wort] as const));
        }
      }
      this.aufz.set(typ, m);
    }
    return m;
  }

  private warneEinmal(schluessel: string, text: string, was: string): void {
    if (this.gewarnt.has(schluessel)) return;
    this.gewarnt.add(schluessel);
    console.warn("[protokoll] " + text + ":", was);
  }
}

/** Stellen von (einschließlich) bis (ausschließlich) sind alle Zahlen. */
function zahlenAn(satz: unknown[], von: number, bis: number): boolean {
  for (let i = von; i < bis; i++) if (typeof satz[i] !== "number") return false;
  return true;
}

/** `topic` und `e` ab Stelle `ab`; fehlende/null-Stellen entfallen. */
function kopfzusatz(satz: unknown[], ab: number): { topic?: string; e?: unknown } {
  const aus: { topic?: string; e?: unknown } = {};
  if (typeof satz[ab] === "string") aus.topic = satz[ab] as string;
  if (satz[ab + 1] !== undefined && satz[ab + 1] !== null) aus.e = satz[ab + 1];
  return aus;
}
